import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';
import api from '../components/api';
import '../css/ProjectPlanningAdmin.css';
import '../css/PageWithSidebar.css';

const emptySlot = () => ({ datum: '', tijd: '', label: { nl: '', en: '' }, maxPlekken: 2 });

const ProjectPlanningAdmin = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    slug: '',
    titel: { nl: '', en: '' },
    beschrijving: { nl: '', en: '' },
    emailOnderwerp: { nl: '', en: '' },
    emailTekst: { nl: '', en: '' },
    slots: [emptySlot()]
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const fetchProjects = useCallback(async () => {
    try {
      const res = await api.get('/api/planning/admin/projects');
      setProjects(res.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const resetForm = () => {
    setForm({
      slug: '',
      titel: { nl: '', en: '' },
      beschrijving: { nl: '', en: '' },
      emailOnderwerp: { nl: '', en: '' },
      emailTekst: { nl: '', en: '' },
      slots: [emptySlot()]
    });
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (project) => {
    setForm({
      slug: project.slug,
      titel: project.titel,
      beschrijving: project.beschrijving,
      emailOnderwerp: project.emailOnderwerp || { nl: '', en: '' },
      emailTekst: project.emailTekst || { nl: '', en: '' },
      slots: project.slots.length > 0 ? project.slots : [emptySlot()]
    });
    setEditingId(project._id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        slots: form.slots.map((s, i) => ({ ...s, slotId: s.slotId || `slot-${i + 1}` }))
      };

      if (editingId) {
        await api.put(`/api/planning/admin/projects/${editingId}`, payload);
      } else {
        await api.post('/api/planning/admin/projects', payload);
      }
      resetForm();
      fetchProjects();
    } catch (err) {
      alert(err.response?.data?.message || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet u zeker dat u dit project en alle inschrijvingen wilt verwijderen?')) return;
    try {
      await api.delete(`/api/planning/admin/projects/${id}`);
      fetchProjects();
    } catch (err) {
      alert('Fout bij verwijderen');
    }
  };

  const toggleActive = async (project) => {
    try {
      await api.put(`/api/planning/admin/projects/${project._id}`, {
        ...project,
        actief: !project.actief
      });
      fetchProjects();
    } catch (err) {
      alert('Fout bij bijwerken');
    }
  };

  const generateKlusLink = async (project) => {
    const existing = project.klusToken && project.klusTokenExpiresAt && new Date(project.klusTokenExpiresAt) > new Date();
    if (existing && !window.confirm('Er bestaat al een geldige klus-link. Een nieuwe genereren maakt de oude meteen ongeldig. Doorgaan?')) return;
    try {
      const res = await api.post(`/api/planning/admin/projects/${project._id}/klus-link`);
      const link = `https://www.vvepolderzicht.nl/klus/${res.data.klusToken}`;
      const verloopt = new Date(res.data.klusTokenExpiresAt).toLocaleDateString('nl-NL');
      try { await navigator.clipboard.writeText(link); } catch (e) { /* clipboard kan falen */ }
      fetchProjects();
      window.prompt(`Klus-link gekopieerd (geldig t/m ${verloopt}). Deel met de klusjesman:`, link);
    } catch (err) {
      alert(err.response?.data?.message || 'Fout bij genereren van klus-link');
    }
  };

  const copyKlusLink = async (project) => {
    const link = `https://www.vvepolderzicht.nl/klus/${project.klusToken}`;
    try {
      await navigator.clipboard.writeText(link);
      alert('Klus-link gekopieerd! Deel deze met de klusjesman.');
    } catch (e) {
      window.prompt('Klus-link (kopieer handmatig):', link);
    }
  };

  const extendKlusLink = async (project) => {
    const input = window.prompt('Met hoeveel dagen verlengen? (de bestaande link blijft werken)', '30');
    if (input === null) return;
    const days = Number(input);
    if (!(days > 0)) { alert('Voer een geldig aantal dagen in.'); return; }
    try {
      const res = await api.patch(`/api/planning/admin/projects/${project._id}/klus-link`, { days });
      const verloopt = new Date(res.data.klusTokenExpiresAt).toLocaleDateString('nl-NL');
      fetchProjects();
      alert(`Klus-link verlengd. Nu geldig t/m ${verloopt}. Dezelfde link blijft werken.`);
    } catch (err) {
      alert(err.response?.data?.message || 'Fout bij verlengen van klus-link');
    }
  };

  const updateSlot = (index, field, value) => {
    const newSlots = [...form.slots];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      newSlots[index] = { ...newSlots[index], [parent]: { ...newSlots[index][parent], [child]: value } };
    } else {
      newSlots[index] = { ...newSlots[index], [field]: value };
    }
    setForm({ ...form, slots: newSlots });
  };

  const addSlot = () => setForm({ ...form, slots: [...form.slots, emptySlot()] });
  const removeSlot = (index) => setForm({ ...form, slots: form.slots.filter((_, i) => i !== index) });

  return (
    <BeheerdersportaalSidebar>
      <div className="pp-admin">
        <div className="pp-admin-header">
          <h1>Planning Projecten</h1>
          <button className="pp-btn-primary" onClick={() => { resetForm(); setShowForm(!showForm); }}>
            {showForm ? 'Annuleren' : '+ Nieuw project'}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSave} className="pp-project-form">
            <h2>{editingId ? 'Project bewerken' : 'Nieuw project'}</h2>

            <div className="pp-form-row">
              <div className="pp-form-group">
                <label>Slug (URL) *</label>
                <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="bijv. schilderwerk-mei-2026" required disabled={!!editingId} />
                {!editingId && <small>URL wordt: /planning/{form.slug || '...'}</small>}
              </div>
            </div>

            <div className="pp-form-row">
              <div className="pp-form-group">
                <label>Titel (NL) *</label>
                <input value={form.titel.nl} onChange={e => setForm({ ...form, titel: { ...form.titel, nl: e.target.value } })} placeholder="Schilderwerk Balkons" required />
              </div>
              <div className="pp-form-group">
                <label>Titel (EN) *</label>
                <input value={form.titel.en} onChange={e => setForm({ ...form, titel: { ...form.titel, en: e.target.value } })} placeholder="Balcony Painting" required />
              </div>
            </div>

            <div className="pp-form-row">
              <div className="pp-form-group">
                <label>Beschrijving (NL) *</label>
                <textarea rows={4} value={form.beschrijving.nl} onChange={e => setForm({ ...form, beschrijving: { ...form.beschrijving, nl: e.target.value } })} placeholder="Uitleg voor bewoners..." required />
              </div>
              <div className="pp-form-group">
                <label>Beschrijving (EN) *</label>
                <textarea rows={4} value={form.beschrijving.en} onChange={e => setForm({ ...form, beschrijving: { ...form.beschrijving, en: e.target.value } })} placeholder="Explanation for residents..." required />
              </div>
            </div>

            <h3>Tijdsloten</h3>
            {form.slots.map((slot, i) => (
              <div key={i} className="pp-slot-form">
                <div className="pp-slot-header">
                  <strong>Slot {i + 1}</strong>
                  {form.slots.length > 1 && <button type="button" className="pp-btn-delete-small" onClick={() => removeSlot(i)}>Verwijder</button>}
                </div>
                <div className="pp-form-row pp-form-row-3">
                  <div className="pp-form-group">
                    <label>Datum *</label>
                    <input type="date" value={slot.datum} onChange={e => updateSlot(i, 'datum', e.target.value)} required />
                  </div>
                  <div className="pp-form-group">
                    <label>Tijd *</label>
                    <input value={slot.tijd} onChange={e => updateSlot(i, 'tijd', e.target.value)} placeholder="08:00 - 13:00" required />
                  </div>
                  <div className="pp-form-group">
                    <label>Max plekken *</label>
                    <input type="number" min="1" value={slot.maxPlekken} onChange={e => updateSlot(i, 'maxPlekken', parseInt(e.target.value) || 1)} required />
                  </div>
                </div>
                <div className="pp-form-row">
                  <div className="pp-form-group">
                    <label>Label (NL) *</label>
                    <input value={slot.label.nl} onChange={e => updateSlot(i, 'label.nl', e.target.value)} placeholder="Zaterdag 28 maart - Ochtend" required />
                  </div>
                  <div className="pp-form-group">
                    <label>Label (EN) *</label>
                    <input value={slot.label.en} onChange={e => updateSlot(i, 'label.en', e.target.value)} placeholder="Saturday March 28 - Morning" required />
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="pp-btn-secondary" onClick={addSlot}>+ Tijdslot toevoegen</button>

            <h3>E-mail instellingen (optioneel)</h3>
            <div className="pp-form-row">
              <div className="pp-form-group">
                <label>E-mail onderwerp (NL)</label>
                <input value={form.emailOnderwerp.nl} onChange={e => setForm({ ...form, emailOnderwerp: { ...form.emailOnderwerp, nl: e.target.value } })} placeholder="Bevestiging schilderwerk" />
              </div>
              <div className="pp-form-group">
                <label>E-mail onderwerp (EN)</label>
                <input value={form.emailOnderwerp.en} onChange={e => setForm({ ...form, emailOnderwerp: { ...form.emailOnderwerp, en: e.target.value } })} placeholder="Confirmation painting" />
              </div>
            </div>
            <div className="pp-form-row">
              <div className="pp-form-group">
                <label>Extra e-mail tekst (NL)</label>
                <textarea rows={2} value={form.emailTekst.nl} onChange={e => setForm({ ...form, emailTekst: { ...form.emailTekst, nl: e.target.value } })} placeholder="Extra info in bevestigingsmail..." />
              </div>
              <div className="pp-form-group">
                <label>Extra e-mail tekst (EN)</label>
                <textarea rows={2} value={form.emailTekst.en} onChange={e => setForm({ ...form, emailTekst: { ...form.emailTekst, en: e.target.value } })} placeholder="Additional info in confirmation email..." />
              </div>
            </div>

            <div className="pp-form-actions">
              <button type="submit" className="pp-btn-primary" disabled={saving}>
                {saving ? 'Opslaan...' : editingId ? 'Bijwerken' : 'Project aanmaken'}
              </button>
              <button type="button" className="pp-btn-secondary" onClick={resetForm}>Annuleren</button>
            </div>
          </form>
        )}

        {loading ? (
          <p>Laden...</p>
        ) : projects.length === 0 ? (
          <p className="pp-empty">Nog geen projecten. Maak een nieuw project aan.</p>
        ) : (
          <div className="pp-projects-list">
            {projects.map(project => (
              <div key={project._id} className={`pp-project-card ${!project.actief ? 'inactive' : ''}`}>
                <div className="pp-project-card-header">
                  <div>
                    <h3>{project.titel.nl}</h3>
                    <p className="pp-project-slug">/planning/{project.slug}</p>
                  </div>
                  <span className={`pp-status ${project.actief ? 'active' : 'inactive'}`}>
                    {project.actief ? 'Actief' : 'Inactief'}
                  </span>
                </div>

                <div className="pp-project-stats">
                  <span>{project.slots?.length || 0} tijdsloten</span>
                  <span>{project.totalBookings || 0} ingeschreven</span>
                  <span>{project.totalWaitlist || 0} wachtlijst</span>
                </div>

                {project.klusToken && project.klusTokenExpiresAt && new Date(project.klusTokenExpiresAt) > new Date() && (
                  <p className="pp-klus-active">🔧 Klus-link actief t/m {new Date(project.klusTokenExpiresAt).toLocaleDateString('nl-NL')}</p>
                )}

                <div className="pp-project-actions">
                  <button className="pp-btn-small" onClick={() => navigate(`/planning-admin/${project._id}`)}>Inschrijvingen</button>
                  <button className="pp-btn-small" onClick={() => startEdit(project)}>Bewerken</button>
                  <button className="pp-btn-small" onClick={() => toggleActive(project)}>{project.actief ? 'Deactiveren' : 'Activeren'}</button>
                  <button className="pp-btn-small pp-btn-danger" onClick={() => handleDelete(project._id)}>Verwijderen</button>
                  <button className="pp-btn-small" onClick={() => { navigator.clipboard.writeText(`https://www.vvepolderzicht.nl/planning/${project.slug}`); alert('Link gekopieerd!'); }}>Link kopieren</button>
                  {project.klusToken && project.klusTokenExpiresAt && new Date(project.klusTokenExpiresAt) > new Date() && (
                    <>
                      <button className="pp-btn-small" onClick={() => copyKlusLink(project)}>📋 Kopieer klus-link</button>
                      <button className="pp-btn-small" onClick={() => extendKlusLink(project)}>⏳ Verleng klus-link</button>
                    </>
                  )}
                  <button className="pp-btn-small" onClick={() => generateKlusLink(project)}>🔧 Nieuwe klus-link</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </BeheerdersportaalSidebar>
  );
};

export default ProjectPlanningAdmin;
